import { useDispatch } from "react-redux";
import { type CustomDispatch } from "../../../redux/types";
import { type RootState } from "../../../redux/store";
import { useBooleanState } from "../../../common/hooks/useBooleanState";
import { type ChangeEvent, type FC, useCallback, useState } from "react";
import { saveConfiguratorAction } from "../redux/actions";
import { addMessage } from "../../../redux/globalMessages/actions";
import { useTrackLCSaveConfigurationClick } from "./useTrackLCClicks";
import { redirectToUrl } from "../../../utils/url/urlUtils";
import { SAVED_CONFIGS_PAGE } from "../../../utils/url/staticUrls";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import { DialogActions, Stack, TextField } from "@mui/material";
import { Dialog, DialogContent, DialogTitle } from "~/common/m3/Dialog/Dialog";

export const useSaveConfigPopup = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch() as CustomDispatch<RootState>;

  const trackSave = useTrackLCSaveConfigurationClick();

  const { val: isModalVisible, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);

  const submitCb = useCallback(
    (name: string) => {
      dispatch(saveConfiguratorAction(name))
        .then((success) => {
          if (success) {
            dispatch(addMessage("info", t("page.linear.saveConfig.result.success")));
            trackSave();
            redirectToUrl(SAVED_CONFIGS_PAGE);
          } else {
            dispatch(addMessage("error", t("page.linear.saveConfig.result.failure")));
          }
        })
        .catch((err) => {
          console.error(err);
          dispatch(addMessage("error", t("page.linear.saveConfig.result.failure")));
        });
      closeModal();
    },
    [closeModal],
  );

  const component = isModalVisible ? <SaveConfigModal onClose={closeModal} onConfirm={submitCb} /> : null;

  return {
    openModal,
    component,
  };
};

interface ModalProps {
  onClose: () => void;
  onConfirm: (name: string) => void;
}

const SaveConfigModal: FC<ModalProps> = ({ onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [pristine, setPristine] = useState(true);

  const confirmCb = useCallback(() => {
    setPristine(false);
    if (!name) {
      return;
    }
    onConfirm(name);
    onClose();
  }, [name, onClose, onConfirm]);

  const updateCb = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPristine(false);
    setName(e.target.value);
  }, []);

  const showError = !pristine && !name;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="desktop-s">
      <DialogTitle>{t("page.linear.saveConfig.header")}</DialogTitle>
      <DialogContent sx={{ overflow: "unset" }}>
        <TextField
          fullWidth
          label={t("page.linear.saveConfig.label")}
          value={name}
          onChange={updateCb}
          error={showError}
          helperText={showError ? t("page.linear.saveConfig.helperLabel") : undefined}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <p className={"m-0 title-medium f-carbon"}>{t("editWishlistNamePopup.mandatory")}</p>
        <Stack gap={1} direction={"row"}>
          <Button variant={"text"} onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button onClick={confirmCb}>{t("generic.save")}</Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
